import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import SideMenu from './SideMenu';
import { getAuthPayload, getUserDisplayName, getUserEmail, getUserRoles } from '../utils/auth';

const DASHBOARD_TITLE_STORAGE_KEY = 'dashboardPageTitle';
const DASHBOARD_YEAR_STORAGE_KEY = 'selectedDashboardYear';
const DASHBOARD_YEAR_OPTIONS_STORAGE_KEY = 'dashboardYearOptions';

const PAGE_META = [
  {
    match: (pathname) => pathname === '/dashboard' || pathname.startsWith('/dashboard/new-application'),
    title: 'All grant applications',
  },
  {
    match: (pathname) => pathname === '/dashboard/programs',
    title: 'Programs',
  },
  {
    match: (pathname) => pathname.startsWith('/dashboard/applications/'),
    title: 'Application Review',
  },
  {
    match: (pathname) => pathname === '/dashboard/applications',
    title: 'Applications',
  },
  {
    match: (pathname) => pathname === '/dashboard/review-rounds',
    title: 'Review Rounds',
  },
  {
    match: (pathname) => pathname === '/dashboard/set-criteria',
    title: 'Set Criteria & Score',
  },
  {
    match: (pathname) => pathname === '/dashboard/decisions',
    title: 'Decisions',
  },
  {
    match: (pathname) => pathname === '/dashboard/laws',
    title: 'Laws & Policy',
  },
  {
    match: (pathname) => pathname === '/dashboard/data-files',
    title: 'Monthly DAT Import',
  },
  {
    match: (pathname) => pathname === '/dashboard/users',
    title: 'User Management',
  },
];

function getPageTitle(pathname) {
  return PAGE_META.find((item) => item.match(pathname))?.title || 'NuGrants';
}

const AppShell = () => {
  const location = useLocation();
  const payload = getAuthPayload();
  const roles = getUserRoles(payload);
  const email = getUserEmail(payload);
  const displayName = getUserDisplayName(payload);
  const [dashboardTitle, setDashboardTitle] = React.useState(
    () => window.sessionStorage.getItem(DASHBOARD_TITLE_STORAGE_KEY) || ''
  );
  const [dashboardYear, setDashboardYear] = React.useState(
    () => window.sessionStorage.getItem(DASHBOARD_YEAR_STORAGE_KEY) || ''
  );
  const [dashboardYearOptions, setDashboardYearOptions] = React.useState(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem(DASHBOARD_YEAR_OPTIONS_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    const syncTitle = (event) => {
      setDashboardTitle(event?.detail || window.sessionStorage.getItem(DASHBOARD_TITLE_STORAGE_KEY) || '');
    };

    const syncYear = (event) => {
      const detail = event?.detail || {};
      setDashboardYear(detail.selectedYear || window.sessionStorage.getItem(DASHBOARD_YEAR_STORAGE_KEY) || '');
      try {
        setDashboardYearOptions(
          Array.isArray(detail.options)
            ? detail.options
            : JSON.parse(window.sessionStorage.getItem(DASHBOARD_YEAR_OPTIONS_STORAGE_KEY) || '[]')
        );
      } catch {
        setDashboardYearOptions([]);
      }
    };

    window.addEventListener('dashboard-title-change', syncTitle);
    window.addEventListener('dashboard-year-change', syncYear);
    window.addEventListener('storage', syncTitle);

    syncTitle();
    syncYear();

    return () => {
      window.removeEventListener('dashboard-title-change', syncTitle);
      window.removeEventListener('dashboard-year-change', syncYear);
      window.removeEventListener('storage', syncTitle);
    };
  }, []);

  const pageTitle =
    location.pathname === '/dashboard'
      ? dashboardTitle || getPageTitle(location.pathname)
      : getPageTitle(location.pathname);

  const showDashboardYearSelector = location.pathname === '/dashboard';

  return (
    <div className="app-shell">
      <SideMenu userRoles={roles} />
      <div className="app-shell-main">
        <header className="app-shell-topbar">
          <div className="app-shell-heading">
            <p className="app-shell-heading-kicker">NuGrants Workspace</p>
            {showDashboardYearSelector ? (
              <div className="app-shell-heading-row">
                <h1>{pageTitle}</h1>
                <div className="app-shell-year-control app-shell-year-control-inline">
                  <label htmlFor="dashboard-year-select" className="filter-label">Year</label>
                  <select
                    id="dashboard-year-select"
                    className="search-input"
                    value={dashboardYear}
                    onChange={(event) => {
                      const nextYear = event.target.value || '';
                      window.sessionStorage.setItem(DASHBOARD_YEAR_STORAGE_KEY, nextYear);
                      window.dispatchEvent(
                        new CustomEvent('dashboard-year-change', {
                          detail: {
                            selectedYear: nextYear,
                            options: dashboardYearOptions,
                          },
                        })
                      );
                    }}
                  >
                    <option value="">All Years</option>
                    {dashboardYearOptions.map((fy) => (
                      <option key={fy} value={fy}>
                        FY{fy} (Oct {fy - 1} – Sep {fy})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <h1>{pageTitle}</h1>
            )}
          </div>
          <div className="app-shell-topbar-actions">
            <div className="app-shell-account">
              <strong>{displayName}</strong>
              {displayName !== email ? <small>{email}</small> : null}
              <span>{roles.join(' • ')}</span>
            </div>
          </div>
        </header>
        <main className="app-shell-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
